// react
import { useMemo } from 'react';

// components
import LoadingArticleContent from './LoadingArticleContent';
import { Chip } from '@/components/atoms/Chip/Chip';
import { Icon } from '@/components/atoms/Icon/Icon';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';
import { Outgrow } from '@/components/organisms/Outgrow/Outgrow';
import { SanityContent } from '@/components/organisms/SanityContent/SanityContent';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';

// store
import { Article as ArticleType } from '@karehero/models';

// utils
import { buildImageUrl } from '@/utils/sanity';

// mui
import { Box, Stack, Typography } from '@mui/material';

/**
 * ArticleContent displays the content for a single article.
 */
export const ArticleContent = ({
  content,
  isLoading = true,
  onBookmark,
  isBookmarked,
  onBack,
}: ArticleContentProps) => {
  // memo
  const publishedDate = useMemo(() => {
    if (!content?._createdAt) {
      return '';
    }
    const date = new Date(content._createdAt);
    return `${date.toLocaleString('default', {
      month: 'long',
    })} ${date.getDate()}, ${date.getFullYear()}`;
  }, [content]);

  const headerImageUrl = useMemo(
    () => buildImageUrl(content?.headerImageUrl, 800),
    [content],
  );

  const readingTime = useMemo(() => {
    if (!content?.content) {
      return '';
    }
    const wordsPerMinute = 200;
    const numberOfWords = content.content.reduce((acc, curr) => {
      if (curr._type === 'block') {
        return (
          acc +
          (curr?.children?.reduce((acc, curr) => {
            return acc + curr.text.split(' ').length;
          }, 0) || 0)
        );
      }
      return acc;
    }, 0);

    const minutes = Math.ceil(numberOfWords / wordsPerMinute);
    return `${minutes} minute read`;
  }, [content]);

  return (
    <ModalPageTemplate onBack={onBack}>
      <PageTemplate
        maxWidth='md'
        headerSlot={
          <LoadingTransition
            isLoading={isLoading}
            loading={
              <Skeleton
                shape='header-image'
                isRounded={false}
                isMargin={false}
              />
            }
            content={
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: { xs: 180, sm: 300 },
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  component='img'
                  src={headerImageUrl}
                  alt={content?.title}
                  onError={({ currentTarget }: any) => {
                    currentTarget.onerror = null;
                    currentTarget.src =
                      'https://cdn.sanity.io/images/x7r1t5o8/production/e8579e2a4c3766cf0cf99a24a9d0679d2d2bbeb4-5322x2365.jpg?h=800';
                  }}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    userSelect: 'none',
                  }}
                />
              </Box>
            }
          />
        }
        contentSlot={
          <LoadingTransition
            isLoading={isLoading}
            loading={<LoadingArticleContent />}
            content={
              <Stack>
                {content?.title && (
                  <Typography
                    variant='h4'
                    sx={{
                      color: 'primary.dark',
                      mb: 4,
                    }}
                  >
                    {content.title}
                  </Typography>
                )}
                {content?.tags && content.tags.length > 0 && (
                  <Stack
                    direction='row'
                    sx={{
                      gap: 1,
                      mb: 4,
                    }}
                  >
                    {content.tags.map((tag) => (
                      <Chip
                        key={`${content.title}-${tag.label}`}
                        label={tag.label}
                      />
                    ))}
                  </Stack>
                )}
                {(content?.publisher || publishedDate) && (
                  <Stack
                    sx={{
                      gap: 0.5,
                      mb: 3,
                    }}
                  >
                    {content?.publisher && (
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                        }}
                      >
                        By {content.publisher}
                      </Typography>
                    )}
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'text.disabled',
                      }}
                    >
                      Published: {publishedDate}
                    </Typography>
                  </Stack>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2,
                    borderTop: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 3,
                  }}
                >
                  <Typography variant='body2'>
                    {readingTime || '0 minute read'}
                  </Typography>
                  <Stack
                    direction='row'
                    onClick={(e) => {
                      e.stopPropagation();
                      content && onBookmark?.(content);
                    }}
                    sx={{
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      userSelect: 'none',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  >
                    <Icon icon='bookmark' type={isBookmarked ? 'fal' : 'fas'} />
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'primary.dark',
                      }}
                    >
                      {isBookmarked ? 'Save' : 'Unsave'}
                    </Typography>
                  </Stack>
                </Box>
                <SanityContent content={content?.content} />
                {content?.outgrowCalculatorUrl && (
                  <Outgrow calculatorUrl={content.outgrowCalculatorUrl} />
                )}
              </Stack>
            }
          />
        }
      />
    </ModalPageTemplate>
  );
};

interface ArticleContentProps {
  content?: ArticleType;
  isLoading?: boolean;
  onBookmark?: (article: Partial<ArticleType>) => void;
  isBookmarked?: boolean;
  onBack?: () => void;
}

ArticleContent.displayName = 'ArticleContent';
export default ArticleContent;
